/**
 * Structured JSON Logger
 * 
 * NO console.log - only structured logging with levels
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
}

class Logger {
  private isDevelopment = import.meta.env.DEV;

  private log(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(context && { context }),
      ...(error && {
        error: {
          name: error.name,
          message: error.message,
          stack: this.isDevelopment ? error.stack : undefined,
        },
      }),
    };

    // In development, pretty print
    if (this.isDevelopment) {
      const emoji = {
        debug: '🔍',
        info: 'ℹ️',
        warn: '⚠️',
        error: '❌',
      }[level];

      console[level === 'debug' || level === 'info' ? 'log' : level](
        `${emoji} [${level.toUpperCase()}]`,
        message,
        context || '',
        error || ''
      );
    } else {
      // In production, log as JSON string
      console[level === 'debug' || level === 'info' ? 'log' : level](JSON.stringify(entry));
    }

    // TODO: Send to external logging service (e.g., Sentry, LogRocket)
    // if (level === 'error' && !this.isDevelopment) {
    //   this.sendToExternalService(entry);
    // }
  }

  debug(message: string, context?: LogContext) {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error, context?: LogContext) {
    this.log('error', message, context, error);
  }

  // Specialized loggers
  apiRequest(method: string, url: string, context?: LogContext) {
    this.debug(`API Request: ${method} ${url}`, context);
  }

  apiResponse(method: string, url: string, status: number, duration: number) {
    this.debug(`API Response: ${method} ${url}`, { status, duration });
  }

  apiError(method: string, url: string, error: Error) {
    this.error(`API Error: ${method} ${url}`, error);
  }

  wsConnect(url: string) {
    this.info(`WebSocket connecting: ${url}`);
  }

  wsConnected(url: string) {
    this.info(`WebSocket connected: ${url}`);
  }

  wsDisconnected(url: string, reason?: string) {
    this.warn(`WebSocket disconnected: ${url}`, { reason });
  }

  wsError(url: string, error: Error) {
    this.error(`WebSocket error: ${url}`, error);
  }

  gpsUpdate(lat: number, lng: number, accuracy: number) {
    this.debug('GPS update', { lat, lng, accuracy });
  }

  gpsError(error: Error) {
    this.error('GPS error', error);
  }

  offlineQueueAdd(action: string, data: unknown) {
    this.info(`Offline queue: added ${action}`, { data });
  }

  offlineQueueSync(count: number) {
    this.info(`Offline queue: syncing ${count} items`);
  }
}

export const logger = new Logger();


















